/**
 * Registry of workspace switch guards (WS5, S2).
 * Guards block workspace switches when submit-type work (counts, scans, forms,
 * manage drafts, running focus sessions) would be lost or misrouted.
 *
 * Distinct from draftGuardRegistry (which guards logout/beforeunload).
 */

export interface SwitchGuard {
  id: string
  /**
   * Short line for the sheet, e.g. "3 counted items in Warehouse A".
   * Returns null when there is no blocking work between `from` and `to`.
   */
  describe: (from: string, to: string) => string | null
  /**
   * Optional save handler. When omitted, the guard sheet offers Discard and Stay only.
   */
  save?: (from: string, to: string) => Promise<void>
  /**
   * Discards the blocking work.
   */
  discard: (from: string, to: string) => void | Promise<void>
}

const guards = new Set<SwitchGuard>()

export const registerSwitchGuard = (guard: SwitchGuard): (() => void) => {
  guards.add(guard)
  return () => {
    guards.delete(guard)
  }
}

export const collectBlockingWork = (from: string, to: string): SwitchGuard[] => {
  const blocking: SwitchGuard[] = []
  for (const guard of Array.from(guards)) {
    try {
      const description = guard.describe(from, to)
      if (description !== null) {
        blocking.push(guard)
      }
    } catch (error) {
      console.error(`[SwitchGuards] Error describing guard ${guard.id}:`, error)
    }
  }
  return blocking
}

/**
 * Test-only utility to clear all registered switch guards.
 */
export const clearSwitchGuards = (): void => {
  guards.clear()
}
